import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { getAllPuzzleTypes, getPuzzleTypeById } from "../../pzl_common/PuzzleTypeConstants";
import { getPubTabLogsColumns } from "./getPubTabLogsColumns";
import PublisherScheduleService from "./PublisherScheduleService";

const AUTO_REFRESH_INTERVAL_MS = 60 * 1000;

const SUCCESS_FILTER_OPTIONS = [
    { value: "all", label: "All" },
    { value: "success", label: "Success" },
    { value: "failed", label: "Failed" },
];

const PUZZLE_TYPE_FILTER_OPTIONS = [
    { value: "all", label: "All" },
    ...getAllPuzzleTypes().map((pt) => ({ value: pt.id, label: pt.name })),
];

export default function PubTabLogs({ schedules = [], defaultScheduleId = "", onError }) {
    const [successFilter, setSuccessFilter] = useState("all");
    const [puzzleTypeFilter, setPuzzleTypeFilter] = useState("all");
    const [scheduleId, setScheduleId] = useState(defaultScheduleId || "");
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRows, setTotalRows] = useState(0);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        if (defaultScheduleId) setScheduleId(defaultScheduleId);
    }, [defaultScheduleId]);

    useEffect(() => {
        let cancelled = false;
        const fetchLogs = async () => {
            setLoading(true);
            const result = await PublisherScheduleService.listScheduleLogs(
                scheduleId || null,
                paginationModel.page,
                paginationModel.pageSize,
                "createdAt:desc",
                successFilter,
                puzzleTypeFilter
            );
            setLoading(false);
            if (cancelled) return;
            if (result.success && result.data) {
                setLogs(result.data.content || []);
                setTotalRows(result.data.page?.totalElements ?? 0);
            } else if (onError) {
                onError("error", result.error || "Failed to fetch logs");
            }
        };
        fetchLogs();
        return () => { cancelled = true; };
    }, [scheduleId, successFilter, puzzleTypeFilter, paginationModel.page, paginationModel.pageSize, refreshTrigger]);

    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(() => setRefreshTrigger((t) => t + 1), AUTO_REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [autoRefresh]);

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap", width: "100%", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="pub-tab-logs-success-label">Status</InputLabel>
                    <Select
                        labelId="pub-tab-logs-success-label"
                        id="pub-tab-logs-success"
                        value={successFilter}
                        label="Status"
                        onChange={(e) => {
                            setSuccessFilter(e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {SUCCESS_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="pub-tab-logs-puzzle-type-label">Puzzle Type</InputLabel>
                    <Select
                        labelId="pub-tab-logs-puzzle-type-label"
                        id="pub-tab-logs-puzzle-type"
                        value={puzzleTypeFilter}
                        label="Puzzle Type"
                        onChange={(e) => {
                            setPuzzleTypeFilter(e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {PUZZLE_TYPE_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="pub-tab-logs-schedule-label">Schedule</InputLabel>
                    <Select
                        labelId="pub-tab-logs-schedule-label"
                        id="pub-tab-logs-schedule"
                        value={scheduleId}
                        label="Schedule"
                        onChange={(e) => {
                            setScheduleId(e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        <MenuItem value="">
                            <em>Select a schedule</em>
                        </MenuItem>
                        {schedules.map((s) => {
                            const typeLabel = getPuzzleTypeById(s.puzzleType)?.name ?? s.puzzleType ?? "—";
                            return (
                                <MenuItem key={s.id} value={s.id}>
                                    {typeLabel} · {s.language} · {s.cron}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            size="small"
                        />
                    }
                    label="Auto refresh"
                />
            </Box>
            <Box sx={{ width: "100%", minWidth: 0 }}>
                <DataGrid
                    rows={logs}
                    columns={getPubTabLogsColumns()}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    getRowHeight={() => "auto"}
                    sx={{ minHeight: 400, width: "100%", margin: 0 }}
                />
            </Box>
        </Box>
    );
}
